import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import {
  BackendPushResponse,
  BackendSuccessResponse,
} from '@freelancer/datastore';
import {
  CartsCollection,
  ContestEntryAction,
  ContestViewContest,
  ContestViewEntry,
  User,
} from '@freelancer/datastore/collections';
import { PaymentsCart } from '@freelancer/payments-cart';
import { ButtonColor } from '@freelancer/ui/button';
import { HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { PictureDisplay, PictureObjectFit } from '@freelancer/ui/picture';
import { RibbonColor, RibbonPlacement } from '@freelancer/ui/ribbon';
import { FontColor, FontType, FontWeight, TextSize } from '@freelancer/ui/text';
import { ContextTypeApi, DestinationApi } from 'api-typings/payments/payments';

@Component({
  selector: 'app-contest-buy-modal',
  template: `
    <fl-bit class="BuyModal" flTrackingSection="ContestViewPage.BuyModal">
      <fl-heading
        i18n="Buy modal title"
        [headingType]="HeadingType.H2"
        [size]="TextSize.MID"
        [flMarginBottom]="Margin.SMALL"
      >
        Buy this Entry
      </fl-heading>

      <fl-bit class="ModalEntryDetails" [flMarginBottom]="Margin.SMALL">
        <fl-text
          i18n="Buy modal entry number"
          [flMarginRight]="Margin.XXXSMALL"
        >
          #{{ entry.number }}
        </fl-text>
        <fl-link
          flTrackingLabel="GoToFreelancerProfile"
          flTrackingReferenceType="freelancer_id"
          flTrackingReferenceId="{{ freelancer?.id }}"
          [link]="freelancer?.profileUrl"
        >
          {{ freelancer?.displayName }}
        </fl-link>
      </fl-bit>

      <fl-bit class="EntryImageContainer" [flMarginBottom]="Margin.XSMALL">
        <fl-ribbon
          [placement]="RibbonPlacement.LEFT"
          [color]="RibbonColor.QUATERNARY"
        >
          {{ sellPrice | flCurrency: contest.currency.code:false:false }}
        </fl-ribbon>
        <fl-picture
          alt="Entry image"
          i18n-alt="Alt text for an contest entry thumbnail"
          class="EntryImageContainer-image"
          [boundedWidth]="true"
          [display]="PictureDisplay.BLOCK"
          [externalSrc]="true"
          [src]="entry.files[0]?.thumbnail900Url"
          [objectFit]="PictureObjectFit.SCALE_DOWN"
        ></fl-picture>
      </fl-bit>

      <fl-text
        i18n="Buy modal total price"
        [weight]="FontWeight.BOLD"
        [flMarginBottom]="Margin.SMALL"
      >
        Total: {{ sellPrice | flCurrency: contest.currency.code }}
      </fl-text>

      <fl-text
        i18n="Buy modal information text"
        [color]="FontColor.MID"
        [size]="TextSize.XXSMALL"
        [flMarginBottom]="Margin.XSMALL"
      >
        You will be redirected to process this payment.
      </fl-text>

      <fl-button
        i18n="Buy modal buy button label"
        flTrackingLabel="BuyEntry"
        flTrackingReferenceType="entry_id"
        flTrackingReferenceId="{{ entry.id }}"
        [color]="ButtonColor.SUCCESS"
        [display]="'block'"
        [flMarginBottom]="Margin.XSMALL"
        [busy]="buyPromise && (buyPromise | async) === null"
        (click)="onBuyEntry()"
      >
        Buy Entry
      </fl-button>

      <ng-container *ngIf="buyPromise | async as response">
        <app-entry-update-error
          *ngIf="response.errorCode"
          [response]="response"
          [source]="ContestEntryAction.BUY"
          [entry]="entry"
        ></app-entry-update-error>
      </ng-container>
    </fl-bit>
  `,
  styleUrls: ['./contest-buy-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContestBuyModalComponent implements OnInit {
  ButtonColor = ButtonColor;
  FontColor = FontColor;
  TextSize = TextSize;
  FontType = FontType;
  FontWeight = FontWeight;
  HeadingType = HeadingType;
  Margin = Margin;
  PictureDisplay = PictureDisplay;
  PictureObjectFit = PictureObjectFit;
  RibbonColor = RibbonColor;
  RibbonPlacement = RibbonPlacement;

  ContestEntryAction = ContestEntryAction;

  @Input() contest: ContestViewContest;
  @Input() entry: ContestViewEntry;
  @Input() freelancer: User;

  buyPromise: Promise<
    BackendSuccessResponse | BackendPushResponse<CartsCollection>
  >;

  sellPrice: number | null;

  constructor(private cart: PaymentsCart) {}

  ngOnInit() {
    const entryPrice = this.entry.sellPrice || this.contest.prize;
    if (entryPrice) {
      this.sellPrice = entryPrice;
    } else {
      this.sellPrice = null;
    }
  }

  onBuyEntry() {
    if (!this.sellPrice) {
      throw new Error(`Invalid amount for buying entry ${this.entry.id}`);
    }

    this.buyPromise = this.cart.handle(
      `Buy entry ${this.entry.id} from contest ${this.contest.id}`,
      {
        destination: DestinationApi.CONTEST_ENTRY_HANDOVER_PAGE,
        payload: `${this.entry.id}`,
      },
      [
        {
          contextType: ContextTypeApi.CONTEST_ENTRY_BUY,
          contextId: `${this.entry.id}`,
          description: `Entry #${this.entry.number}`,
          currencyId: this.contest.currency.id,
          amount: this.sellPrice,
        },
      ],
    );
  }
}
